import React from 'react';

import {SINGLE_DATASET_INPUT_STEP_DATA_NAME} from 'analysis-tool/AnalysisStepDataNames';
import {GetAnalysisPipelineStep} from 'analysis-tool/AnalysisTypes';
import {findAndAssertSingleStepDataFrameId} from 'analysis-tool/data/findAndAssertStepDataFrameIds';
import {DataFrame} from 'data-frames/DataFrameTypes';
import {DEFAULT_DATA_FRAME_ROW_LIMIT} from 'data-frames/graphql/GetDataFrame';
import {
  GetPersistedDataFrame,
  GetPersistedDataFrameVariables,
} from 'data-frames/wrapper/__generated/GetPersistedDataFrame';
import {GET_PERSISTED_DATA_FRAME_QUERY} from 'data-frames/wrapper/PersistedDataFrameTableWrapper';
import {PipelineStepType} from 'graphql-client/__generated/graphqlGlobalTypes';
import BenchlingQuery from 'graphql-client/BenchlingQuery';
import {assertIsAccessible} from 'graphql-client/InaccessibleInfo';
import {PipelineStepConfig} from 'pipelines/PipelineTypes';
import {getPipelineStepConfig} from 'pipelines/StepShapeUtils';
import SelectDropdown from 'ui-library/dropdowns/SelectDropdown';
import {CenteredHorizontalThrobber} from 'ui-library/HorizontalThrobber';
import {MenuHeight, MenuWidth} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';

type EQUALS_FILTER_CONFIG = Extract<
  PipelineStepConfig<PipelineStepType.FILTER_DATASET, 0>['filter'],
  {operation: 'EQUALS'}
>;

export function getInitialFilterDropdownValue(filterStep: GetAnalysisPipelineStep): string {
  const filter = getPipelineStepConfig(filterStep, PipelineStepType.FILTER_DATASET, 0)
    .filter as EQUALS_FILTER_CONFIG;
  return filter.value as string;
}

export interface DataFrameFilterDropdownProps {
  className?: string;
  disabled: boolean;
  displayName: string;
  filterStep: GetAnalysisPipelineStep;
  onSelectValue: (newFilter: EQUALS_FILTER_CONFIG) => void;
  selectedValue: JSONScalar | null;
}

export default function DataFrameFilterDropdown({
  className,
  disabled,
  displayName,
  filterStep,
  onSelectValue,
  selectedValue,
}: DataFrameFilterDropdownProps): JSX.Element {
  const filter = getPipelineStepConfig(filterStep, PipelineStepType.FILTER_DATASET, 0)
    .filter as EQUALS_FILTER_CONFIG;
  return (
    <BenchlingQuery<GetPersistedDataFrame, GetPersistedDataFrameVariables>
      query={GET_PERSISTED_DATA_FRAME_QUERY}
      variables={{
        dataFrameId: findAndAssertSingleStepDataFrameId(
          filterStep,
          SINGLE_DATASET_INPUT_STEP_DATA_NAME,
          true
        ),
        limit: DEFAULT_DATA_FRAME_ROW_LIMIT,
      }}
      renderLoading={() => <CenteredHorizontalThrobber />}
    >
      {({dataFrame}) => {
        const dataFrameJson = assertIsAccessible(dataFrame).dataFrameJson as DataFrame;
        const uniqueValues = [...new Set(dataFrameJson.rows.map((row) => row[filter.columnSystemName]))];

        return (
          <SelectDropdown
            className={className}
            disabled={disabled}
            displayName={displayName}
            menu={
              <SearchableMenu
                height={MenuHeight.MEDIUM}
                width={MenuWidth.FULL}
                optionDatas={uniqueValues.map((value) => ({
                  name: value as string,
                  value,
                }))}
                onSelect={(value) => {
                  onSelectValue({...filter, value});
                }}
                selectedValue={selectedValue}
              />
            }
          />
        );
      }}
    </BenchlingQuery>
  );
}
